import { useQuery } from "@tanstack/react-query";
import api, { money } from "@/lib/api";
import { PageHeader } from "./AdminShared";
import { StatusBadge, fmtDate } from "@/components/StatusBadge";
import { Users, ShoppingCart, Zap, Clock, DollarSign, AlertCircle } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="glass rounded-2xl p-6 hover:border-purple-500/30 transition-colors">
    <div className="flex items-center justify-between">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <div className="font-heading text-3xl font-extrabold text-white mt-4">{value}</div>
    <div className="text-sm text-slate-400 mt-1">{label}</div>
  </div>
);

export default function AdminDashboard() {
  const { data: stats } = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data });

  const cards = [
    { icon: Users, label: "Total Customers", value: stats?.total_customers ?? 0, accent: "bg-blue-500/15 text-blue-400", testid: "stat-customers" },
    { icon: ShoppingCart, label: "Total Orders", value: stats?.total_orders ?? 0, accent: "bg-purple-500/15 text-purple-400", testid: "stat-orders" },
    { icon: Zap, label: "Active Subscriptions", value: stats?.active_subscriptions ?? 0, accent: "bg-emerald-500/15 text-emerald-400", testid: "stat-active" },
    { icon: Clock, label: "Expired Subscriptions", value: stats?.expired_subscriptions ?? 0, accent: "bg-slate-500/15 text-slate-400", testid: "stat-expired" },
    { icon: AlertCircle, label: "Pending Orders", value: stats?.pending_orders ?? 0, accent: "bg-yellow-500/15 text-yellow-400", testid: "stat-pending" },
    { icon: DollarSign, label: "Total Revenue", value: money(stats?.revenue ?? 0), accent: "bg-cyan-500/15 text-cyan-400", testid: "stat-revenue" },
  ];

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Overview of your Saad IPTV business" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
        {cards.map((c) => <StatCard key={c.label} {...c} />)}
      </div>
      <div className="glass rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10">
          <h2 className="font-heading font-semibold text-white">Recent Orders</h2>
        </div>
        <div className="divide-y divide-white/5">
          {(stats?.recent_orders || []).length === 0 ? (
            <div className="px-6 py-10 text-center text-slate-500">No orders yet.</div>
          ) : stats.recent_orders.map((o) => (
            <div key={o.id} className="px-6 py-4 flex items-center justify-between" data-testid={`recent-order-${o.id}`}>
              <div>
                <div className="text-white font-medium">{o.user_name} · {o.package_name}</div>
                <div className="text-xs text-slate-500">{fmtDate(o.created_at)} · {o.user_email}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-white font-medium">{money(o.amount, o.currency)}</span>
                <StatusBadge status={o.status} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
